import { FILTERS, isSpecialFilter, SPECIAL_FILTER_COLUMNS } from '@/models/common/filters';
import {
  AboveValueConfig,
  AfterDateValueConfig,
  AfterFilterValueConfig,
  BeforeDateValueConfig,
  BeforeFilterValueConfig,
  BetweenDateValueConfig,
  InTheLastValueConfig,
  UserInteractionsValueConfigInteractionsCount,
  UserConversationsCountValueConfigConversationsCount,
  UserConversationLengthValueConfigInteractionsCount,
} from '@/api/generated/model';
import { formatToHuman } from '@/utils/date.util';
import { translate } from '@/utils/translations';
import { GenericFilter } from '@/comps/FilterPicker/FilterPicker';

export interface FilterSplit {
  regularFilters: GenericFilter[];
  specialFilters: GenericFilter[];
}

export { isSpecialFilter, SPECIAL_FILTER_COLUMNS };

/** Filter kinds that must never be mapped to interaction-aggregates `group_by`. */
export const FILTER_ONLY_GROUP_BY_KINDS = [
  ...SPECIAL_FILTER_COLUMNS,
  FILTERS.columnInteractionId,
  FILTERS.columnUserInteractionPosition,
  FILTERS.kindInputOutput,
  FILTERS.kindIsNegativeIntentV2,
  FILTERS.kindSharedBy,
] as const;

export const isFilterOnlyGroupByKind = (column: string): boolean => {
  return (FILTER_ONLY_GROUP_BY_KINDS as readonly string[]).includes(column);
};

/**
 * Splits filters into regular and special filters based on filter column type.
 */
export const splitFilters = (filters: GenericFilter[]): FilterSplit => {
  const regularFilters = filters.filter((filter) => !isSpecialFilter(filter.column));
  const specialFilters = filters.filter((filter) => isSpecialFilter(filter.column));

  return {
    regularFilters,
    specialFilters,
  };
};

export const formatDateConfigs = (
  config:
    | BeforeDateValueConfig
    | AfterDateValueConfig
    | BetweenDateValueConfig
    | InTheLastValueConfig
    | AboveValueConfig
    | BeforeFilterValueConfig
    | AfterFilterValueConfig,
): string => {
  if (config.kind === 'before') {
    return formatBefore(config);
  } else if (config.kind === 'after') {
    return formatAfter(config);
  } else if (config.kind === 'between') {
    return formatBetween(config);
  } else if (config.kind === 'in_the_last') {
    return formatInTheLast(config);
  } else {
    console.error(`Unknown date config kind: ${config.kind}`);
    return '';
  }
};

const formatBetween = (values: BetweenDateValueConfig) =>
  `${translate('date_config_kind', 'between')} ${formatToHuman(new Date(values.value.start).valueOf())} and ${formatToHuman(
    new Date(values.value.end ?? Date.now()).valueOf(),
  )}`;
const formatAfter = (values: AfterDateValueConfig | AfterFilterValueConfig) =>
  typeof values.value === 'string'
    ? `${translate('date_config_kind', 'after')} ${formatToHuman(new Date(values.value).valueOf())}`
    : `${translate('date_config_kind', 'after')} ${values.value.column} is one of ${values.value.values.join(', ')}`;
const formatBefore = (values: BeforeDateValueConfig | BeforeFilterValueConfig) =>
  typeof values.value === 'string'
    ? `${translate('date_config_kind', 'before')} ${formatToHuman(new Date(values.value).valueOf())}`
    : `${translate('date_config_kind', 'before')} ${values.value.column} is one of ${values.value.values.join(', ')}`;
const formatInTheLast = (values: InTheLastValueConfig) =>
  `${translate('date_config_kind', 'in_the_last')} ${values.value} ${values.granularity}s`;

type ValueConfigUnion =
  | UserInteractionsValueConfigInteractionsCount
  | UserConversationsCountValueConfigConversationsCount
  | UserConversationLengthValueConfigInteractionsCount;

export const formatValueConfigs = (config: ValueConfigUnion): string => {
  // For UserInteractionsValueConfigInteractionsCount (has per property)
  if ('per' in config) {
    const perString = config.per ? `per ${config.per}` : '';

    if (config.kind === 'above') {
      return `${translate('value_config_kind', config.kind)} ${config.value} interactions ${perString}`;
    } else if (config.kind === 'below') {
      return `${translate('value_config_kind', config.kind)} ${config.value} interactions ${perString}`;
    } else if (config.kind === 'exactly') {
      return `${translate('value_config_kind', config.kind)} ${config.value} interactions ${perString}`;
    } else if (config.kind === 'between') {
      return `${translate('value_config_kind', config.kind)} ${config.value[0]} and ${
        config.value[1]
      } interactions ${perString}`;
    }
  } else {
    // For the new value config types (no per property)
    if (config.kind === 'above') {
      return `${translate('value_config_kind', config.kind)} ${config.value}`;
    } else if (config.kind === 'below') {
      return `${translate('value_config_kind', config.kind)} ${config.value}`;
    } else if (config.kind === 'exactly') {
      return `${translate('value_config_kind', config.kind)} ${config.value}`;
    } else if (config.kind === 'between') {
      return `${translate('value_config_kind', config.kind)} ${config.value[0]} and ${config.value[1]}`;
    }
  }

  return '';
};
